import { useMemo, useState } from "react";
import { Link } from "react-router";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAgenciaFunilComparativo, type AgenciaFunilVagas } from "@/hooks/useAgenciaFunilComparativo";

const LIMITE_LINHAS_PADRAO = 15;

type ColunaOrdenavel =
  | "agencia_nome"
  | "vagas_criadas"
  | "vagas_aprovadas"
  | "vagas_reprovadas"
  | "taxa_aprovacao"
  | "tempo_medio_dias_ate_terminal";

interface ColunaConfig {
  chave: ColunaOrdenavel;
  rotulo: string;
  alinhamentoDireita?: boolean;
}

const COLUNAS: ColunaConfig[] = [
  { chave: "agencia_nome", rotulo: "Agência" },
  { chave: "vagas_criadas", rotulo: "Criadas", alinhamentoDireita: true },
  { chave: "vagas_aprovadas", rotulo: "Aprovadas", alinhamentoDireita: true },
  { chave: "vagas_reprovadas", rotulo: "Reprovadas", alinhamentoDireita: true },
  { chave: "taxa_aprovacao", rotulo: "Taxa de aprovação", alinhamentoDireita: true },
  {
    chave: "tempo_medio_dias_ate_terminal",
    rotulo: "Tempo médio até desfecho",
    alinhamentoDireita: true,
  },
];

/** Compara duas linhas por uma coluna; valores nulos sempre ficam por último,
 *  independentemente da direção de ordenação escolhida. */
function compararLinhas(
  linhaA: AgenciaFunilVagas,
  linhaB: AgenciaFunilVagas,
  coluna: ColunaOrdenavel,
): number {
  const valorA = linhaA[coluna];
  const valorB = linhaB[coluna];
  if (typeof valorA === "string" || typeof valorB === "string") {
    return String(valorA ?? "").localeCompare(String(valorB ?? ""), "pt-BR");
  }
  if (valorA === null && valorB === null) return 0;
  if (valorA === null) return 1;
  if (valorB === null) return -1;
  return valorA - valorB;
}

/**
 * Card "Funil de vagas por agência" do Dashboard interno: comparativo entre
 * agências (criadas/aprovadas/reprovadas/taxa de aprovação/tempo médio até
 * desfecho), via v_agencia_funil_vagas (sql/161). Ordenável ao clicar no
 * cabeçalho; mostra as 15 primeiras por padrão, com "Mostrar todas" expandindo.
 */
export function AgenciaFunilTable() {
  const { data, isLoading, isError } = useAgenciaFunilComparativo();
  const [colunaOrdenacao, setColunaOrdenacao] = useState<ColunaOrdenavel>("vagas_criadas");
  const [ordemDescendente, setOrdemDescendente] = useState(true);
  const [mostrarTodas, setMostrarTodas] = useState(false);

  const linhasOrdenadas = useMemo(() => {
    const linhas = [...(data ?? [])];
    linhas.sort((linhaA, linhaB) => {
      const resultado = compararLinhas(linhaA, linhaB, colunaOrdenacao);
      return ordemDescendente ? -resultado : resultado;
    });
    return linhas;
  }, [data, colunaOrdenacao, ordemDescendente]);

  const linhasVisiveis = mostrarTodas
    ? linhasOrdenadas
    : linhasOrdenadas.slice(0, LIMITE_LINHAS_PADRAO);
  const quantidadeOculta = linhasOrdenadas.length - linhasVisiveis.length;

  const alternarOrdenacao = (coluna: ColunaOrdenavel) => {
    if (coluna === colunaOrdenacao) {
      setOrdemDescendente((atual) => !atual);
    } else {
      setColunaOrdenacao(coluna);
      setOrdemDescendente(true);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Funil de vagas por agência</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : isError ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            Não foi possível carregar o funil de vagas por agência.
          </p>
        ) : linhasOrdenadas.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            Nenhuma agência com vaga registrada ainda.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    {COLUNAS.map((coluna) => (
                      <TableHead
                        key={coluna.chave}
                        className={coluna.alinhamentoDireita ? "text-right" : undefined}
                      >
                        <button
                          type="button"
                          onClick={() => alternarOrdenacao(coluna.chave)}
                          className={`inline-flex items-center gap-1 hover:text-foreground ${
                            coluna.alinhamentoDireita ? "flex-row-reverse" : ""
                          }`}
                        >
                          {coluna.rotulo}
                          {colunaOrdenacao === coluna.chave ? (
                            ordemDescendente ? (
                              <ArrowDown className="h-3 w-3" />
                            ) : (
                              <ArrowUp className="h-3 w-3" />
                            )
                          ) : (
                            <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />
                          )}
                        </button>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {linhasVisiveis.map((linha) => (
                    <TableRow key={linha.agencia_id}>
                      <TableCell className="font-medium">
                        <Link to={`/agencias/${linha.agencia_id}`} className="hover:underline">
                          {linha.agencia_nome}
                        </Link>
                      </TableCell>
                      <TableCell className="text-right">{linha.vagas_criadas}</TableCell>
                      <TableCell className="text-right">{linha.vagas_aprovadas}</TableCell>
                      <TableCell className="text-right">{linha.vagas_reprovadas}</TableCell>
                      <TableCell className="text-right">
                        {linha.taxa_aprovacao !== null
                          ? `${(linha.taxa_aprovacao * 100).toFixed(1)}%`
                          : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        {linha.tempo_medio_dias_ate_terminal !== null
                          ? `${linha.tempo_medio_dias_ate_terminal.toFixed(1)} dias`
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {quantidadeOculta > 0 && (
              <div className="pt-2">
                <Button variant="ghost" size="sm" onClick={() => setMostrarTodas(true)}>
                  Mostrar todas ({quantidadeOculta} a mais)
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default AgenciaFunilTable;
